import React, { useContext, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Check, ExternalLink, MessageCircle } from 'lucide-react';
import { LanguageContext, useNavigation } from './LanguageContext';
import { logoMap } from './logos/logoMap';
import { getProjectsData } from './data';
import { ProjectCard } from './ProjectCard';
import { ParticleWave } from './ParticleWave';
import CraftAndCode from './units/CraftAndCodeOptimized';

const VentureLabHero = React.lazy(() => import('./VentureLabHero').then((module) => ({ default: module.VentureLabHero })));
const CreatonHero = React.lazy(() => import('./CreatonHero').then((module) => ({ default: module.CreatonHero })));

interface UnitPageProps {
    unit: {
        id: string;
        name: string;
        description?: string;
        href: string;
        externalUrl?: string;
        tags?: string[];
        pageContent?: {
            title: string;
            paragraph: string;
            whatWeDeliver?: {
                title: string;
                items: string[];
            };
        };
    };
    openChat: (source?: string) => void;
}

const unitThemes: Record<string, { glow: string; border: string; number: string; button: string }> = {
    arhideko: {
        glow: 'bg-amber-300/30 dark:bg-amber-500/15',
        border: 'hover:border-amber-400/70 dark:hover:border-amber-500/60',
        number: 'text-amber-600 dark:text-amber-400',
        button: 'hover:bg-amber-500 hover:border-amber-500 hover:text-black',
    },
    buildhub: {
        glow: 'bg-orange-300/30 dark:bg-orange-500/15',
        border: 'hover:border-orange-400/70 dark:hover:border-orange-500/60',
        number: 'text-orange-600 dark:text-orange-400',
        button: 'hover:bg-orange-500 hover:border-orange-500 hover:text-black',
    },
    creaton: {
        glow: 'bg-fuchsia-300/25 dark:bg-fuchsia-500/15',
        border: 'hover:border-fuchsia-400/70 dark:hover:border-fuchsia-500/60',
        number: 'text-fuchsia-600 dark:text-fuchsia-400',
        button: 'hover:bg-fuchsia-500 hover:border-fuchsia-500 hover:text-white',
    },
    craftcode: {
        glow: 'bg-cyan-300/25 dark:bg-cyan-500/15',
        border: 'hover:border-cyan-400/70 dark:hover:border-cyan-500/60',
        number: 'text-cyan-600 dark:text-cyan-400',
        button: 'hover:bg-cyan-400 hover:border-cyan-400 hover:text-black',
    },
    kateinteriors: {
        glow: 'bg-rose-300/25 dark:bg-rose-500/15',
        border: 'hover:border-rose-400/70 dark:hover:border-rose-500/60',
        number: 'text-rose-600 dark:text-rose-400',
        button: 'hover:bg-rose-400 hover:border-rose-400 hover:text-black',
    },
    venturelab: {
        glow: 'bg-indigo-300/25 dark:bg-indigo-500/15',
        border: 'hover:border-indigo-400/70 dark:hover:border-indigo-500/60',
        number: 'text-indigo-600 dark:text-indigo-400',
        button: 'hover:bg-indigo-500 hover:border-indigo-500 hover:text-white',
    },
};

const defaultTheme = unitThemes.buildhub;

const UnitPage: React.FC<UnitPageProps> = ({ unit, openChat }) => {
    const { t, language } = useContext(LanguageContext);
    const { navigate } = useNavigation();
    const LogoComponent = unit?.id ? logoMap[unit.id] : null;
    const theme = unitThemes[unit?.id] || defaultTheme;
    const services = unit?.pageContent?.whatWeDeliver?.items || [];
    const relatedUnits = (t?.data?.units || []).filter((item: any) => item?.id && item.id !== unit.id);
    const featuredProjects = getProjectsData(t)
        .filter(Boolean)
        .filter((project: any) => unit?.tags?.some((tag) => project?.kpis?.includes(tag)));
    const isImmersiveUnit = ['creaton', 'craftcode', 'venturelab'].includes(unit.id);

    useEffect(() => {
        // Turn old hash-based company links into their clean public URL.
        if (window.location.pathname === '/' && window.location.hash.startsWith('#/')) {
            const cleanPath = window.location.hash.substring(1).split('#')[0];
            window.history.replaceState(null, '', `${cleanPath}${window.location.search}`);
        }
        window.scrollTo(0, 0);
    }, [unit]);

    const localizedHref = (href: string) => `${href}${language !== 'en' ? `?lang=${language}` : ''}`;

    const handleInternalLink = (event: React.MouseEvent<HTMLAnchorElement>, href: string) => {
        event.preventDefault();
        navigate(href);
    };

    return (
        <main className="min-h-screen bg-white text-gray-900 transition-colors duration-300 dark:bg-black dark:text-white">
            {isImmersiveUnit ? (
                <React.Suspense fallback={<div className="min-h-screen bg-black" aria-label="Loading" />}>
                    {unit.id === 'creaton' && <CreatonHero openChat={openChat} />}
                    {unit.id === 'craftcode' && <CraftAndCode openChat={openChat} />}
                    {unit.id === 'venturelab' && <VentureLabHero openChat={openChat} />}
                </React.Suspense>
            ) : (
                <>
            <div className="fixed inset-0 z-0 pointer-events-none opacity-30 dark:opacity-40" aria-hidden="true">
                <ParticleWave interactive={false} />
            </div>
            <section className="relative z-10 isolate overflow-hidden border-b border-gray-200 bg-white/90 backdrop-blur-sm dark:border-gray-800 dark:bg-black/85">
                <div className={`absolute -right-24 top-12 -z-10 h-80 w-80 rounded-full blur-3xl ${theme.glow}`} aria-hidden="true" />
                <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8 lg:py-20">
                    <a
                        href={localizedHref('/')}
                        onClick={(event) => handleInternalLink(event, '/')}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-gray-500 transition-colors hover:text-gray-950 dark:text-gray-400 dark:hover:text-white"
                    >
                        <ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
                        {t?.unitPage?.backToHome || 'Back to Home'}
                    </a>

                    <div className="mt-12 grid items-center gap-12 lg:grid-cols-[minmax(0,1.2fr)_minmax(20rem,0.8fr)] lg:gap-20">
                        <div>
                            <p className="text-sm font-bold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400">
                                Buildhub GLOBAL <span aria-hidden="true">/</span> {unit.name}
                            </p>
                            <h1 className="mt-5 max-w-4xl text-5xl font-black leading-[0.98] tracking-[-0.04em] text-gray-950 dark:text-white sm:text-6xl lg:text-7xl">
                                {unit?.pageContent?.title || unit.name}
                            </h1>
                            <p className="mt-7 max-w-3xl text-lg leading-8 text-gray-600 dark:text-gray-300 sm:text-xl">
                                {unit?.pageContent?.paragraph || unit.description || ''}
                            </p>

                            {unit.tags && unit.tags.length > 0 && (
                                <ul className="mt-8 flex flex-wrap gap-2" aria-label={`${unit.name} expertise`}>
                                    {unit.tags.map((tag) => (
                                        <li key={tag} className="rounded-full border border-gray-300 bg-white/70 px-3.5 py-1.5 text-sm font-medium text-gray-700 backdrop-blur dark:border-gray-700 dark:bg-black/50 dark:text-gray-200">
                                            {tag}
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <div className="mt-10 flex flex-col gap-3 sm:flex-row">
                                <button
                                    type="button"
                                    onClick={() => openChat(`unit_${unit.id}_hero`)}
                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-black px-7 py-3.5 text-base font-bold text-white transition-transform hover:-translate-y-0.5 hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 dark:bg-white dark:text-black"
                                >
                                    <MessageCircle className="h-5 w-5" aria-hidden="true" />
                                    {t?.assistant?.title || 'Talk to our assistant'}
                                </button>
                                {unit.externalUrl && (
                                    <a
                                        href={unit.externalUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => window.gtag?.('event', 'partner_click', {
                                            unit: unit.id,
                                            destination_host: new URL(unit.externalUrl).hostname.replace(/^www\./, ''),
                                        })}
                                        className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-7 py-3.5 text-base font-bold text-gray-900 transition-colors hover:border-gray-950 dark:border-gray-700 dark:text-white dark:hover:border-white"
                                    >
                                        {t?.unitPage?.visitSite || 'Visit Website'}
                                        <ExternalLink className="h-4 w-4" aria-hidden="true" />
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="relative mx-auto flex aspect-square w-full max-w-md items-center justify-center overflow-hidden rounded-[2rem] border border-gray-200 bg-gray-50 p-12 shadow-2xl shadow-black/5 dark:border-gray-800 dark:bg-gray-950 dark:shadow-black/40">
                            <div className={`absolute inset-1/4 rounded-full blur-3xl ${theme.glow}`} aria-hidden="true" />
                            <div className="relative z-10 flex h-full w-full items-center justify-center text-gray-950 dark:text-white">
                                {LogoComponent ? <LogoComponent /> : <span className="text-4xl font-black">{unit.name}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {services.length > 0 && (
                <section className="relative z-10 bg-gray-50/95 py-20 backdrop-blur-sm dark:bg-gray-950/90 sm:py-28">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <h2 className="text-3xl font-black tracking-tight text-gray-950 dark:text-white sm:text-5xl">
                                {unit.pageContent?.whatWeDeliver?.title}
                            </h2>
                        </div>
                        <ol className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                            {services.map((service, index) => (
                                <li
                                    key={service}
                                    className={`group min-h-44 rounded-2xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl dark:border-gray-800 dark:bg-black ${theme.border}`}
                                >
                                    <div className="flex items-start justify-between gap-5">
                                        <span className={`text-sm font-black tabular-nums ${theme.number}`}>{String(index + 1).padStart(2, '0')}</span>
                                        <Check className="h-5 w-5 text-gray-400 group-hover:text-current" aria-hidden="true" />
                                    </div>
                                    <p className="mt-8 text-lg font-bold leading-7 text-gray-900 dark:text-white">{service}</p>
                                </li>
                            ))}
                        </ol>
                    </div>
                </section>
            )}

            {featuredProjects.length > 0 && (
                <section className="relative z-10 bg-white/95 py-20 backdrop-blur-sm dark:bg-black/90 sm:py-28">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl font-black tracking-tight text-gray-950 dark:text-white sm:text-5xl">
                            {t?.unitPage?.featuredProjectsTitle || 'Featured Projects'}
                        </h2>
                        <div className={`mt-12 grid gap-8 ${featuredProjects.length === 1 ? 'place-items-center' : 'md:grid-cols-2'}`}>
                            {featuredProjects.map((project: any, index: number) => (
                                <div key={project?.title || index} className={featuredProjects.length === 1 ? 'w-full max-w-2xl' : 'w-full'}>
                                    <ProjectCard {...project} />
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
                </>
            )}

            {relatedUnits.length > 0 && (
                <section className="relative z-20 border-y border-gray-200 bg-white py-20 dark:border-gray-800 dark:bg-black sm:py-24">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="max-w-3xl">
                            <h2 className="text-3xl font-black tracking-tight text-gray-950 dark:text-white sm:text-4xl">
                                {t?.units?.title || 'Our Units'}
                            </h2>
                            <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-300">
                                {t?.units?.description || ''}
                            </p>
                        </div>
                        <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                            {relatedUnits.map((related: any) => (
                                <a
                                    key={related.id}
                                    href={localizedHref(related.href)}
                                    onClick={(event) => handleInternalLink(event, related.href)}
                                    className="group flex min-h-28 flex-col justify-between rounded-xl border border-gray-200 p-5 transition-all hover:-translate-y-0.5 hover:border-gray-950 hover:shadow-lg dark:border-gray-800 dark:hover:border-white"
                                >
                                    <span className="font-bold text-gray-950 dark:text-white">{related.name}</span>
                                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 group-hover:text-gray-950 dark:text-gray-400 dark:group-hover:text-white">
                                        {t?.units?.exploreUnit || 'Explore unit'}
                                        <ArrowRight className="h-4 w-4 rtl:rotate-180" aria-hidden="true" />
                                    </span>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            <section className="relative z-20 bg-black py-20 text-white dark:bg-gray-950 sm:py-28">
                <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-black tracking-tight sm:text-5xl">
                        {t?.unitPage?.ctaTitle || ''}
                    </h2>
                    <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-gray-300">
                        {t?.unitPage?.ctaDescription || ''}
                    </p>
                    <button
                        type="button"
                        onClick={() => openChat(`unit_${unit.id}_cta`)}
                        className={`mt-9 inline-flex items-center justify-center gap-2 rounded-lg border border-white bg-white px-8 py-4 text-lg font-bold text-black transition-all hover:-translate-y-0.5 ${theme.button}`}
                    >
                        <MessageCircle className="h-5 w-5" aria-hidden="true" />
                        {t?.assistant?.title || 'Talk to our assistant'}
                    </button>
                </div>
            </section>
        </main>
    );
};

export default UnitPage;
